/* PDP store availablility sheet */
'use strict';

import { queryFirst, queryAll, addClass, removeClass, removeAttribute, setAttribute, toFormData, hasClass } from '../domUtil';
import { getJSON, geolocate, setItemInLocalStorage, getItemFromLocalStorage } from '../util';
import { includeGoogleMaps, getLatLong } from '../googleMaps';
import {
    PREFERRED_STORE_ID,
    HIDDEN_CLASS,
    ACTIVE_CLASS,
    NOT_AVAILABLE_CLASS,
    SELECTED_CLASS,
    COLOR,
    IN_STOCK_STATUS,
    NON_EDITING_KEYS
} from '../constants';

import { normalizeRadioGroup } from './accessibleRadioGroup';
import { updateCartTotals, updatePromoCodeDetails, validateBasket, updateCartItems, updateGWP, updateApplePayButton } from '../cart/cart';
import { toggleLocationIcon } from './pickUpInStore/ispuHelper';
import { SPU_HIDDEN_CLASS } from './shippingPreference/constants';
import { updateVisibilityOfLowInventoryMsg } from './shippingPreference/shippingPreferenceHelper';
import { sizeButtonTemplate } from '../templates';

const $body = $('body');

let ispuContainer = queryFirst('.sheet-page.pickup-in-store-content .pickup-in-store-container');
let ispuModalContainer = $('.sheet.modal[id*="pick-up-in-store"]');
const pickUpInStoreButton = queryFirst('.btn-in-store-pickup');
const isRecognizedUser = pickUpInStoreButton && pickUpInStoreButton.dataset.isRecognizedUser === 'true';
const ispuClosestParent = $('.product-detail, .cart-page');
let targetModal = null;
let currentTargetIspuButton = null;

/**
 * Get store inventory for all variants and updates size button availability.
 * @param {string} pid - master or VG product ID
 * @param {string} storeId - the store to check inventory for
 * @param {HTMLElement} container - ISPU container with size buttons
 */
function updateSizeAvailabilityForStore(pid, storeId, container) {
    const inventoryUrl = window.lillyUtils?.shippingPreferencesConfig?.actionUrl;
    if (!inventoryUrl || !pid || !storeId) return;

    const productDetails = queryFirst('.pickup-in-store-product', container);
    const sizeList = queryFirst('.size-container', productDetails);
    if (!sizeList) return;

    getJSON(inventoryUrl, 'GET', { pid, storeID: storeId }, data => {
        if (!data || !data.success || !data.variants) return;

        const sizeBtns = queryAll('.size-btn', productDetails);
        if (!sizeBtns) return;

        sizeBtns.forEach(btn => {
            const { variationId: sku } = btn.dataset;
            const variant = data.variants[sku];
            const hasStock = variant && variant.availabilityStatus === IN_STOCK_STATUS;
            if (hasStock) removeClass(btn, NOT_AVAILABLE_CLASS);
            else addClass(btn, NOT_AVAILABLE_CLASS);
        });

        normalizeRadioGroup(sizeList, '.size-btn[role="radio"]', {
            selectedClass: SELECTED_CLASS,
            notAvailableClass: NOT_AVAILABLE_CLASS
        });
    });
}

/**
 * Toggle sheet view
 * @param {string} sheetSelector
 * @param {string} [id] - product id suffix for product-set modals
 */
function showSheet(sheetSelector, id) {
    const activeIspuModal = ispuModalContainer.filter(id ? `[id$=${id}]` : ':has(.active)')[0];
    if (!activeIspuModal) return;

    removeClass(queryAll('.sheet-page', activeIspuModal), ACTIVE_CLASS);

    if (id) {
        addClass(queryAll('.sheet-page.pickup-in-store-content', activeIspuModal), HIDDEN_CLASS);
    }

    const sheetContent = queryFirst('.sheet-page' + sheetSelector, activeIspuModal);
    if (sheetContent) {
        removeClass(sheetContent, HIDDEN_CLASS);
        addClass(sheetContent, ACTIVE_CLASS);
    }
}
/**
 * This method checks if the user is on cart page
 * @returns {boolean} is cart page or not
 */
function isInCartPage() {
    const cartPage = queryFirst('.cart-page');
    return !!cartPage;
}

/**
 * This method checks if the user is on PDP page
 * @returns {boolean} is PDP page or not
 */
function isInPdpPage() {
    const pdpPage = queryFirst('.pdp-container');
    return !!pdpPage;
}

/**
 * This method returns ISPU cart line items details
 * @returns {Object} ISPU cart line items details
 */
function getIspuCartLineItemDetails() {
    const ispuContainer = queryFirst('.cart-page .cart-products-ispu');
    const ispuLineItems = queryAll('.cart-product-line-item', ispuContainer);
    let ispuLineItemsData = [];
    if (ispuLineItems) {
        ispuLineItems.forEach(ispuLineItem => {
            const { pid, uuid } = ispuLineItem.dataset;
            const quantity = queryFirst('.quantity', ispuLineItem).value;
            ispuLineItemsData.push({
                pid,
                uuid,
                quantity
            });
        });
    }
    return ispuLineItemsData;
}

/**
 * Get size variation data via Product-Variation and build size buttons in the ISPU sheet.
 * @param {string} vgId - Variation group ID
 * @param {string} variationUrl - Product-Variation endpoint base URL
 * @param {HTMLElement} productDetails - element in the ISPU sheet
 * @param {Function} callback - called after buttons are built (or on error)
 */
function getAndBuildSizeButtons(vgId, variationUrl, productDetails, callback) {
    const url = new URL(variationUrl, window.location.origin);
    url.searchParams.set('pid', vgId);
    getJSON(url.toString(), 'GET', null, data => {
        const sizeContainer = queryFirst('.size-container', productDetails);
        if (!sizeContainer || !data || !data.product) {
            callback();
            return;
        }

        const sizeAttr = (data.product.variationAttributes || []).find(attr => attr.attributeId === 'size');
        if (!sizeAttr || !sizeAttr.values || !sizeAttr.values.length) {
            callback();
            return;
        }

        const { selectedText, outOfStock, label, attrValue } = sizeContainer.dataset;
        const sizes = sizeAttr.values;

        const html = sizes.map((size, index) => {
            const sizeValue = size.displayValue || size.value;
            const ariaLabel = (label || 'Select') + ' ' + (attrValue || 'Size') + ' ' + sizeValue;
            const ariaDescribedby = 'ispu-size-' + sizeValue + '-' + index;

            const variantData = {
                selected: false,
                forceOutOfStock: !!size.forceOutOfStock,
                inStock: size.inStock !== false,
                value: sizeValue,
                url: size.url || '',
                sizeId: size.variantID || ''
            };

            return sizeButtonTemplate(variantData, ariaLabel, ariaDescribedby, selectedText || '', outOfStock || '', sizes.length);
        }).join('');

        sizeContainer.innerHTML = html;

        normalizeRadioGroup(sizeContainer, '.size-btn[role="radio"]', {
            selectedClass: SELECTED_CLASS,
            notAvailableClass: NOT_AVAILABLE_CLASS
        });

        callback();
    }, () => {
        callback();
    });
}
/**
 * This method handles the store results content
 * @param {Object} data store results
 */
function handleStoreResults(data) {
    const modalId = currentTargetIspuButton?.dataset.targetid || (isInCartPage() ? 'pick-up-in-store' : currentTargetIspuButton?.dataset.pid);
    // showSheet should be called before finding the active ISPU modal container
    showSheet('.pickup-in-store-content', modalId);
    const activeIspuModalContainer = ispuModalContainer.filter(modalId ? `[id$=${modalId}]` : ':has(.active)');
    const closestIspuContainer = activeIspuModalContainer.find('.sheet-page.pickup-in-store-content .pickup-in-store-container')[0];
    if (!closestIspuContainer) {
        ispuModalContainer.find('.sheet-dialog').spinner().stop();
        return;
    }
    const noStoresMessage = queryFirst('.store-locator-no-results', closestIspuContainer);
    const findStoresForm = queryFirst('.find-stores-form', closestIspuContainer);
    const storeResults = queryFirst('.pick-up-in-stores', closestIspuContainer);
    const productDetails = queryFirst('.pickup-in-store-product', closestIspuContainer);
    const pickupAddToCartBlock = queryFirst('.js-sp-add-to-cart', closestIspuContainer.closest('.pickup-in-store-modal'));
    const pickupAddToCartSelector = isInCartPage() ? '.store-confirm-button' : '.add-to-cart';
    const pickupAddToCartEl = queryFirst(pickupAddToCartSelector, pickupAddToCartBlock);
    const selectedStoreInput = queryFirst('.select-store-input:checked:not([disabled])', closestIspuContainer);
    const sizeList = queryFirst('.size-container', productDetails);
    addClass(queryFirst('.geo-location-error', closestIspuContainer), HIDDEN_CLASS);

    if (pickupAddToCartEl && !selectedStoreInput) {
        pickupAddToCartEl.disabled = true;
    }

    if (data.storesResultsHtml) {
        storeResults.innerHTML = data.storesResultsHtml;
        noStoresMessage.textContent = '';
        if (!data.isCartPage) {
            removeClass(productDetails, HIDDEN_CLASS);
        }
        removeClass(storeResults, HIDDEN_CLASS);
        addClass(noStoresMessage, HIDDEN_CLASS);
        addClass(findStoresForm, HIDDEN_CLASS);
        removeClass(pickupAddToCartEl, SPU_HIDDEN_CLASS);
        removeClass(pickupAddToCartBlock, HIDDEN_CLASS);

        const selectStoreInput = queryAll('.select-store-input');

        let selectedStoreId = null;
        const savedStoreId = window.lillyUtils.shippingPreferencesConfig.storeId;

        if ((isInCartPage() || savedStoreId === 'null' || !savedStoreId) && data.preferredStore) {
            setItemInLocalStorage(PREFERRED_STORE_ID, data.preferredStore.ID);
            selectedStoreId = data.preferredStore.ID;
        } else {
            selectedStoreId = savedStoreId;
        }

        if (sizeList) {
            const { stores } = data;
            if (stores) {
                const sizeBtns = queryAll('.size-btn', productDetails);

                sizeBtns.forEach(btn => {
                    const { variationId: sku } = btn.dataset;
                    let hasStock = false;
                    let selectedStoreDetails = null;
                    if (selectedStoreId && savedStoreId !== 'null') {
                        // If prefered store is set and selected store is same as prefered store
                        // then show size availability based on prefered store
                        // else find selected store in store search results
                        if (data.preferredStore && data.preferredStore.ID === selectedStoreId) {
                            selectedStoreDetails = data.preferredStore;
                        } else {
                            selectedStoreDetails = stores.find(store => store.ID === selectedStoreId) || null;
                        }
                    }
                    // If selected store details are available
                    // check stock for the sku in selected store
                    // else check stock across all stores in search results
                    if (selectedStoreDetails) {
                        hasStock = selectedStoreDetails.allVariantsInventory?.some(record => record.id === sku && parseInt(record.quantity, 10) > 0) ?? false;
                    } else {
                        hasStock = stores?.some(store => !!parseInt(store.allVariantsInventory?.find(record => record.id === sku)?.quantity, 10)) ?? true;
                    }
                    if (hasStock) removeClass(btn, NOT_AVAILABLE_CLASS);
                    else addClass(btn, NOT_AVAILABLE_CLASS);
                });
            }

            // Store search results may change which sizes are available, update roving tabindex
            normalizeRadioGroup(sizeList, '.size-btn[role="radio"]', {
                selectedClass: SELECTED_CLASS,
                notAvailableClass: NOT_AVAILABLE_CLASS
            });
        }

        if (selectedStoreId) {
            removeAttribute(selectStoreInput, 'checked');
            const selectedStoreInput = queryFirst(`.select-store-input[data-store-id*="${selectedStoreId}"]:not([disabled])`);
            const selectedSizeInput = queryFirst('.size-btn.selected');
            setAttribute(selectedStoreInput, 'checked', true);
            removeClass(pickupAddToCartBlock, HIDDEN_CLASS);

            if (pickupAddToCartEl) {
                if (selectedStoreInput && (isInCartPage() || selectedSizeInput)) {
                    pickupAddToCartEl.disabled = false;
                    removeClass(pickupAddToCartEl, SPU_HIDDEN_CLASS);
                } else {
                    pickupAddToCartEl.disabled = true;
                }
            }
            if (!isInCartPage() && selectedStoreInput && selectedSizeInput) {
                const { variationid } = selectedSizeInput.dataset;
                updateVisibilityOfLowInventoryMsg(ispuClosestParent[0], variationid);
            }
        }
    } else if (data.noStoresErrorMessage) {
        noStoresMessage.innerHTML = data.noStoresErrorMessage;
        addClass([storeResults, pickupAddToCartBlock], HIDDEN_CLASS);
        removeClass(noStoresMessage, HIDDEN_CLASS);
        removeClass(findStoresForm, HIDDEN_CLASS);
    } else {
        removeClass(pickupAddToCartEl, SPU_HIDDEN_CLASS);
    }

    ispuModalContainer.find('.sheet-dialog').spinner().stop();
}

/**
 * This method handles the store confirmation results content
 * @param {Object} data store results
 */
function handleConfirmStoreResults(data) {
    const storeConfirm = queryFirst('.pick-up-confirm-section', ispuContainer);
    const storeResults = queryFirst('.pick-up-in-stores', ispuContainer);
    const storeForm = queryFirst('.find-stores-form', ispuContainer);
    storeConfirm.innerHTML = data.storesResultsHtml;
    if (data.storesResultsHtml) {
        removeClass(storeConfirm, HIDDEN_CLASS);
        addClass([storeResults, storeForm], HIDDEN_CLASS);
    }
    ispuModalContainer.find('.sheet-dialog').spinner().stop();
}

/**
 * This method is an error handler of ajax call
 */
function errorCallBack() {
    ispuModalContainer.find('.sheet-dialog').spinner().stop();
}

$body.on('product:pdpSizeSelected', ($event, data) => {
    const { target } = $event;
    const { productContainer } = data;

    if (ispuModalContainer.is(':visible')) {
        const { actionUrl, pid, googleKey, productName, swatchName, masterId } = currentTargetIspuButton?.dataset ?? {};

        const isCartPage = isInCartPage();
        const ispuContainerDataset = ispuContainer.dataset;
        const mixinData = {
            products: pid,
            isCartPage
        };

        includeGoogleMaps(googleKey);

        ispuContainerDataset.pid = pid;
        ispuContainerDataset.productName = productName;
        ispuContainerDataset.swatchName = swatchName;
        ispuContainerDataset.masterId = masterId;

        if (isRecognizedUser) {
            const storeId = getItemFromLocalStorage(PREFERRED_STORE_ID);

            if (storeId) {
                mixinData.storeID = storeId;
            }
        }
        const searchStoreResultTitle = queryFirst('.search-store-toggle', ispuContainer);
        if (searchStoreResultTitle) {
            mixinData.searchStoreResultTitle = searchStoreResultTitle.textContent.trim();
        }

        if (ispuContainerDataset.selectStoreView === 'true') {
            mixinData.selectStoreView = true;
        }
        if (isCartPage) {
            const quantity = queryFirst('.quantity', target.closest('.cart-product-line-item')).value;
            mixinData.quantity = quantity;
            const { uuid } = target.dataset;
            ispuContainerDataset.uuid = uuid;
            ispuContainerDataset.quantity = quantity;
            addClass(queryFirst('.pickup-in-store-product'), HIDDEN_CLASS);
        }

        targetModal = queryFirst('.sheet.modal[id*="pick-up-in-store"]');
        const address = queryAll('.find-store-input', targetModal)[1].value;

        if (address) {
            getLatLong(handleInStore, { address, actionUrl });
        } else {
            geolocate(
                data => {
                    ispuModalContainer.find('.sheet-dialog').spinner().start();
                    toggleLocationIcon(true, ispuContainer);
                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                },
                mixinData,
                data => {
                    toggleLocationIcon(false, ispuContainer);
                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                }
            );
        }
    }
});

// Event listener to retrieve store results on click of Pick up in store in PDP
$('.pdp-in-store-inventory, .cart-page').on('click', '.btn-in-store-pickup', event => {
    const { target } = event;
    const { actionUrl, pid, googleKey, productName, productUrl, swatchName, masterId, targetid } = target.dataset;
    const isCartPage = isInCartPage();

    const ispuContainerDataset = ispuContainer.dataset;
    const mixinData = {
        products: pid,
        isCartPage
    };
    includeGoogleMaps(googleKey);
    $('.sheet-dialog.pickup-in-store-modal').attr('aria-hidden', false);

    showSheet('.pickup-in-store-content', targetid || pid);

    ispuContainerDataset.pid = pid;
    ispuContainerDataset.productName = productName;
    ispuContainerDataset.swatchName = swatchName;
    ispuContainerDataset.masterId = masterId;
    currentTargetIspuButton = target;
    if (isRecognizedUser) {
        const storeId = getItemFromLocalStorage(PREFERRED_STORE_ID);

        if (storeId) {
            mixinData.storeID = storeId;
        }
    }
    if (ispuContainerDataset.selectStoreView === 'true') {
        mixinData.selectStoreView = true;
    }
    if (isCartPage) {
        const quantity = queryFirst('.quantity', target.closest('.cart-product-line-item')).value;
        mixinData.quantity = quantity;
        const { uuid } = target.dataset;
        ispuContainerDataset.uuid = uuid;
        ispuContainerDataset.quantity = quantity;
        if (hasClass(target, 'ispu-oos-product-alternative')) {

            // Exclude the OOS line item from cart availability check
            ispuContainerDataset.excludeLineItemUUID = uuid;
            ispuContainerDataset.isOosReplacement = 'true';
            mixinData.excludeLineItemUUID = uuid;
            const activeIspuModal = ispuModalContainer.filter(':has(.active)')[0] || ispuModalContainer[0];
            const productDetails = queryFirst('.pickup-in-store-product', activeIspuModal);
            if (productDetails) {
                removeClass(productDetails, HIDDEN_CLASS);
                const cartItem = target.closest('.cart-product-line-item');

                // Product name
                const nameEl = queryFirst('.pickup-product-name', productDetails);
                if (nameEl) nameEl.textContent = productName || '';

                // PDP links on image and product name
                if (productUrl) {
                    const pdpLinks = productDetails.querySelectorAll('.pickup-pdp-link');
                    pdpLinks.forEach(link => { link.href = productUrl; });
                }

                // Color
                const colorEl = queryFirst('.selected-color', productDetails);
                if (colorEl) colorEl.textContent = swatchName ? COLOR + swatchName : '';

                if (cartItem) {
                    // Price from cart line item
                    const priceEl = queryFirst('.pickup-price', productDetails);
                    const cartPrice = queryFirst('.line-item-price-paid', cartItem);
                    if (priceEl && cartPrice) priceEl.textContent = cartPrice.textContent.trim();

                    // Image from cart line item
                    const cartImg = queryFirst('.cart-item-image img', cartItem);
                    const pickupImg = queryFirst('.pickup-product-img img', productDetails);
                    if (cartImg && pickupImg) {
                        pickupImg.src = cartImg.src || cartImg.dataset.src || '';
                        pickupImg.srcset = cartImg.srcset || cartImg.dataset.srcset || '';
                        pickupImg.alt = cartImg.alt || '';
                        addClass(queryFirst('.pickup-product-img .no-images', productDetails), HIDDEN_CLASS);
                    }
                }

                // get size buttons from Product-Variation, then trigger store search
                const { vgId, productVariationUrl } = target.dataset;
                const sizeEl = queryFirst('[data-attr-name="Size"]', target.closest('.cart-product-line-item'));
                const selectedSize = sizeEl ? sizeEl.dataset.attrValue : '';
                if (vgId && productVariationUrl) {
                    getAndBuildSizeButtons(vgId, productVariationUrl, productDetails, () => {
                        // Pre-select the cart item's current size
                        if (selectedSize) {
                            const matchingBtn = queryFirst('.size-btn[data-attr-value="' + selectedSize + '"]', productDetails);
                            if (matchingBtn) {
                                matchingBtn.click();
                                return;
                            }
                        }

                        const oosTargetModal = queryFirst('.sheet.modal[id*="pick-up-in-store"]');
                        const oosInputs = queryAll('.find-store-input', oosTargetModal);
                        const oosAddress = oosInputs && oosInputs[1] ? oosInputs[1].value : '';
                        if (oosAddress) {
                            getLatLong(handleInStore, { address: oosAddress, actionUrl });
                        } else {
                            geolocate(
                                data => {
                                    ispuModalContainer.find('.sheet-dialog').spinner().start();
                                    toggleLocationIcon(true, ispuContainer);
                                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                                },
                                mixinData,
                                data => {
                                    toggleLocationIcon(false, ispuContainer);
                                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                                }
                            );
                        }
                    });
                    return;
                }
            }
        } else {
            delete ispuContainerDataset.excludeLineItemUUID;
            delete ispuContainerDataset.isOosReplacement;
            addClass(queryFirst('.pickup-in-store-product'), HIDDEN_CLASS);
        }
    }
    const targetModal = queryFirst('.sheet.modal[id*="pick-up-in-store"]');
    const address = queryAll('.find-store-input', targetModal)[1].value;

    if (address) {
        getLatLong(handleInStore, { address, actionUrl });
    } else {
        geolocate(
            data => {
                ispuModalContainer.find('.sheet-dialog').spinner().start();
                toggleLocationIcon(true, ispuContainer);
                getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
            },
            mixinData,
            data => {
                toggleLocationIcon(false, ispuContainer);
                getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
            }
        );
    }
});

// Event listener to select a store from ISPU drawer
ispuClosestParent.on('click', '.btn-select-store', event => {
    const { target } = event;
    const { storeId } = target.dataset;
    const productContainer = target.closest('.product-detail');
    // prevent store unselect
    if (!target.checked) {
        event.preventDefault();
        return false;
    }

    const selectStoreInputs = queryAll('.js-select-store-input');
    selectStoreInputs.forEach(function (selectStoreInput) {
        selectStoreInput.checked = false;
    });

    target.checked = true;

    const modal = target.closest('.sheet[id*="pick-up-in-store"]');
    const pickupAddToCartSelector = isInCartPage() ? '.store-confirm-button' : '.add-to-cart';
    const pickupAddToCartBlock = modal && queryFirst('.js-sp-add-to-cart', modal);
    const pickupAddToCartEl = pickupAddToCartBlock && queryFirst(pickupAddToCartSelector, pickupAddToCartBlock);

    if (pickupAddToCartBlock) {
        removeClass(pickupAddToCartBlock, HIDDEN_CLASS);
    }
    if (pickupAddToCartEl) {
        pickupAddToCartEl.disabled = false;
        removeClass(pickupAddToCartEl, SPU_HIDDEN_CLASS);
    }

    $body.trigger('product:updateStoreInventory', { storeId, productContainer });

    // OOS ISPU: update size buttons for the newly selected store
    if (isInCartPage()) {
        const activeIspuModalContainer = ispuModalContainer.filter(':has(.active)');
        const closestIspuContainer = activeIspuModalContainer.find('.sheet-page.pickup-in-store-content .pickup-in-store-container')[0];
        const { masterId } = ispuContainer.dataset;
        if (closestIspuContainer && masterId) {
            updateSizeAvailabilityForStore(masterId, storeId, closestIspuContainer);
        }
    }
});

ispuClosestParent.on('click', '.sheet-page .modal-back', () => {
    showSheet('.pickup-in-store-content');

    addClass(queryFirst('.find-stores-form', ispuContainer), HIDDEN_CLASS);
});

ispuClosestParent.on('click', '.search-store-toggle', event => {
    event.preventDefault();
    const { target } = event;
    showSheet('.pickup-in-store-search-form');
    removeClass(queryFirst('.find-stores-form', target.closest('.pickup-in-store-container')), HIDDEN_CLASS);
});

// Handle size button selection inside ISPU sheet for cart OOS
$('.cart-page').on('click', '.pickup-in-store-product .size-btn', event => {
    const { currentTarget } = event;

    const sizeContainer = currentTarget.closest('.size-container');
    if (!sizeContainer) return;

    const allBtns = queryAll('.size-btn', sizeContainer);
    removeClass(allBtns, SELECTED_CLASS);
    addClass(currentTarget, SELECTED_CLASS);

    allBtns.forEach(btn => {
        btn.setAttribute('aria-checked', btn === currentTarget ? 'true' : 'false');
        btn.tabIndex = btn === currentTarget ? 0 : -1;
    });

    // Update the pid so Cart-ChangeToStore uses the selected variant
    const { variationId } = currentTarget.dataset;
    if (variationId) {
        ispuContainer.dataset.pid = variationId;
    }

    // Re-run store search to update store availability for the selected size
    const { actionUrl } = currentTargetIspuButton?.dataset ?? {};
    if (actionUrl) {
        const sizeMixinData = { products: variationId, isCartPage: true };
        const { excludeLineItemUUID } = ispuContainer.dataset;
        if (excludeLineItemUUID) {
            sizeMixinData.excludeLineItemUUID = excludeLineItemUUID;
        }
        if (isRecognizedUser) {
            const storeId = getItemFromLocalStorage(PREFERRED_STORE_ID);
            if (storeId) {
                sizeMixinData.storeID = storeId;
            }
        }
        const sizeModal = queryFirst('.sheet.modal[id*="pick-up-in-store"]');
        const sizeInputs = queryAll('.find-store-input', sizeModal);
        const sizeAddress = sizeInputs && sizeInputs[1] ? sizeInputs[1].value : '';
        if (sizeAddress) {
            getLatLong(handleInStore, { address: sizeAddress, actionUrl });
        } else {
            geolocate(
                data => {
                    ispuModalContainer.find('.sheet-dialog').spinner().start();
                    toggleLocationIcon(true, ispuContainer);
                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                },
                sizeMixinData,
                data => {
                    toggleLocationIcon(false, ispuContainer);
                    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
                }
            );
        }
    }
});

// Event Listener for edit pick up store link in cart page
$('.cart-page').on('click', '.js-edit-store-pickup', event => {
    ispuContainer.dataset.editStore = 'true';
    const products = null;
    const { actionUrl, googleKey } = event.target.dataset;
    includeGoogleMaps(googleKey);
    addClass(queryFirst('.pickup-in-store-product'), HIDDEN_CLASS);
    const mixinData = {
        products,
        isCartPage: true
    };
    geolocate(
        data => {
            ispuModalContainer.find('.sheet-dialog').spinner().start();
            toggleLocationIcon(true, ispuContainer);
            getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
        },
        mixinData,
        data => {
            toggleLocationIcon(false, ispuContainer);
            getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
        }
    );
});

/**
 * This method retrieves store results based on zipcode, city or state
 * @param {Object} locationData google location data based on input value
 */
function handleInStore(locationData) {
    const { latitude, longitude, address, actionUrl, forceGeoLocation } = locationData;
    const ispuContainerDataset = ispuContainer.dataset;
    const isEditStore = JSON.parse(ispuContainerDataset.editStore || 'false');
    let products = null;
    if (!isEditStore) {
        products = ispuContainerDataset.pid + ':' + ispuContainerDataset.quantity;
    }
    const isCartPage = isInCartPage();
    const data = {
        products,
        latitude,
        longitude,
        inputSearchParameter: address,
        isCartPage,
        forceGeoLocation
    };
    const { excludeLineItemUUID } = ispuContainerDataset;
    if (excludeLineItemUUID) {
        data.excludeLineItemUUID = excludeLineItemUUID;
    }
    data.selectStoreView = isInPdpPage();
    if (isRecognizedUser) {
        const storeId = getItemFromLocalStorage(PREFERRED_STORE_ID);
        if (storeId) {
            data.storeID = storeId;
        }
    }
    ispuModalContainer.find('.sheet-dialog').spinner().start();
    getJSON(actionUrl, 'GET', data, handleStoreResults, errorCallBack);
}

// Event listener to retrieve store results based on input search in pick up in store drawer
ispuClosestParent.on('submit', '.find-stores-form', event => {
    event.preventDefault();
    const { target } = event;
    const actionUrl = target.getAttribute('action');
    const address = queryFirst('.find-store-input', target.closest('.find-stores-form')).value;
    queryAll('.find-store-input').forEach(function (input) {
        input.value = address;
    });
    if (address) {
        targetModal = target.closest('.sheet-page.active.pickup-in-store-container');
        getLatLong(handleInStore, {
            address,
            actionUrl
        });
    }
});

/**
 * This method retrieves store results based on zipcode, city or state after preferred store slection
 * @param {Object} locationData google location data based on input value
 */
function handlePreferredStore(locationData) {
    const url = queryFirst('.preferred-store-input').dataset.actionUrl;
    const isCartPage = isInCartPage();
    const { latitude, longitude, address, storeID, products } = locationData;
    const data = {
        products,
        latitude,
        longitude,
        inputSearchParameter: address,
        storeID,
        isCartPage,
        selectStoreView: isInPdpPage()
    };
    ispuModalContainer.find('.sheet-dialog').spinner().start();
    getJSON(url, 'POST', data, handleStoreResults, errorCallBack);
}

// Event listener to update preferred store and rerender store results
ispuClosestParent.on('click', '.preferred-store-input', event => {
    const { target } = event;
    const isEditStore = JSON.parse(ispuContainer.dataset.editStore);
    let products = null;
    if (!isEditStore) {
        products = ispuContainer.dataset.pid;
    }
    const address = queryFirst('.find-store-input').value;
    const { dataset } = target;
    const { actionUrl, storeId } = dataset;
    const isCartPage = isInCartPage();
    const mixinData = {
        products,
        storeID: storeId,
        isCartPage
    };
    if (isCartPage && !isEditStore) {
        mixinData.quantity = ispuContainer.dataset.quantity;
    }
    if (address) {
        mixinData.address = address;
        getLatLong(handlePreferredStore, mixinData);
    } else {
        const isCartPage = isInCartPage();
        geolocate(
            data => {
                ispuModalContainer.find('.sheet-dialog').spinner().start();
                getJSON(actionUrl, 'POST', data, handleStoreResults);
            },
            {
                products,
                storeID: storeId,
                isCartPage,
                selectStoreView: isInPdpPage()
            },
            data => {
                getJSON(actionUrl, 'POST', data, handleStoreResults);
            }
        );
    }
});

// Event listener to render pick up in store confirmation template
ispuClosestParent.on('click', '.pick-up-confirm-button', event => {
    const { dataset } = event.target;
    const { actionUrl, storeId } = dataset;
    const isCartPage = isInCartPage();
    const data = {
        storeID: storeId,
        isCartPage
    };
    ispuModalContainer.find('.sheet-dialog').spinner().start();
    getJSON(actionUrl, 'GET', data, handleConfirmStoreResults, errorCallBack);
});

// Event listener to ISPU store confirmation in cart
ispuClosestParent.on('click', '.store-confirm-button', async event => {
    const selectedStoreInput = queryFirst('.select-store-input:checked:not([disabled])');
    if (!selectedStoreInput) return;
    const { target } = event;
    const { actionUrl } = target.dataset;
    const { storeId } = selectedStoreInput.dataset;
    const isEditStore = JSON.parse(ispuContainer.dataset.editStore);
    let reqData;
    if (isEditStore) {
        const productsInfo = JSON.stringify(getIspuCartLineItemDetails());
        reqData = {
            productsInfo,
            storeID: storeId
        };
    } else {
        const { pid, uuid, quantity, isOosReplacement } = ispuContainer.dataset;
        reqData = {
            pid,
            uuid,
            quantity,
            storeID: storeId
        };
        if (isOosReplacement) {
            reqData.isOosReplacement = isOosReplacement;
        }
    }
    ispuModalContainer.find('.sheet-dialog').spinner().start();

    try {
        const res = await fetch(actionUrl, {
            method: 'POST',
            body: toFormData(reqData)
        });

        if (!res.ok) throw new Error('response was not OK');
        const data = await res.json();

        if (data.error) {
            const { lineItemErrorMessage } = data;
            ispuModalContainer.find('.sheet-dialog').spinner().stop();
            ispuModalContainer.modal('hide');
            $('.add-to-cart-messages').remove();
            if ($('.add-to-cart-messages').length === 0) {
                $('body').append('<div class="add-to-cart-messages"></div>');
            }
            $('.add-to-cart-messages').append('<div class="alert alert-danger add-to-basket-alert text-center" role="alert">' + lineItemErrorMessage + '</div>');
            setTimeout(function () {
                $('.add-to-basket-alert').remove();
            }, 5000);
        } else {
            const { cartProductsHtml, totals } = data;
            const { discountsHtml } = totals;
            const couponsEl = queryFirst('.coupon-wrapper');

            if (couponsEl && discountsHtml) {
                couponsEl.innerHTML = discountsHtml;
            }
            if (cartProductsHtml) {
                document.cookie = `dwapupreq=${new Date().getTime()}; `;
                updateCartItems(data);
                updateGWP(data);
                updatePromoCodeDetails(data.totals.discounts, data.selectedShippingMethod);
                updateCartTotals(data);
                validateBasket(data);
                updateApplePayButton(data);
                $('body').trigger('cart:update');
                ispuModalContainer.find('.sheet-dialog').spinner().stop();
                ispuModalContainer.modal('hide');
            }
        }
    } catch (err) {
        console.error(`pickUpInStore error: ${err.message}`);
        ispuModalContainer.find('.sheet-dialog').spinner().stop();
    }
});

// Event listener to toggle store details accordion label
ispuClosestParent.on('click', '.store-locator-container .store-btn-link', event => {
    const { target } = event;
    queryFirst('.collapsed', target).classList.toggle(HIDDEN_CLASS);
    queryFirst('.expanded', target).classList.toggle(HIDDEN_CLASS);
});

// Event listener to show stores to select on click of change location on pickup confirmation page
ispuClosestParent.on('click', '.change-location-link', () => {
    addClass(queryFirst('.pick-up-confirm-section'), HIDDEN_CLASS);
    removeClass(queryAll('.pick-up-in-stores, .find-stores-form'), HIDDEN_CLASS);
});

// Event listener to remove no stores error message on change of ISPU search input
ispuClosestParent.on('focus', '.find-store-input', event => {
    event.target.dataset.prevFindStoreValue = event.target.value;
});

ispuClosestParent.on('keyup', '.find-store-input', event => {
    if (NON_EDITING_KEYS.includes(event.key)) return;

    const { target } = event;
    const prevValue = target.dataset.prevFindStoreValue ?? '';
    if (target.value === prevValue) return;

    target.dataset.prevFindStoreValue = target.value;

    const modal = target.closest('.sheet[id*="pick-up-in-store"]');
    const contentContainer = modal && queryFirst('.sheet-page.pickup-in-store-content .pickup-in-store-container', modal);
    const pickupAddToCartBlock = modal && queryFirst('.js-sp-add-to-cart', modal);

    if (modal) {
        queryFirst('.store-locator-no-results', modal).textContent = '';
    }
    if (contentContainer) {
        const storeResults = queryFirst('.pick-up-in-stores', contentContainer);
        if (storeResults) storeResults.innerHTML = '';
        addClass(queryFirst('.geo-location-error', contentContainer), HIDDEN_CLASS);
    }
    if (pickupAddToCartBlock) {
        addClass(pickupAddToCartBlock, HIDDEN_CLASS);
    }

    const findStoreSubmit = queryFirst('.find-store-submit', target.closest('.pickup-in-store-container'));
    if (findStoreSubmit) {
        if (target.value) {
            findStoreSubmit.removeAttribute('disabled');
        } else {
            findStoreSubmit.disabled = true;
        }
    }
});

$('.sheet[id*="pick-up-in-store"]').on('hidden.bs.modal', () => {
    const activeIspuModalContainer = ispuModalContainer.filter(':has(.active)');
    const activeIspuContainer = activeIspuModalContainer.find('.sheet-page.pickup-in-store-content .pickup-in-store-container')[0] || ispuContainer;
    const findStore = queryFirst('.find-store-input', activeIspuContainer);
    if (findStore) findStore.value = '';
    removeClass(queryAll('.pickup-in-store-product, .find-stores-form', activeIspuContainer), HIDDEN_CLASS);
    queryFirst('.pick-up-in-stores', activeIspuContainer).innerHTML = '';
    const noStoresMessage = queryFirst('.store-locator-no-results', activeIspuContainer);
    const confirmSection = queryFirst('.pick-up-confirm-section', activeIspuContainer);
    const geolocateError = queryFirst('.geo-location-error', activeIspuContainer);
    confirmSection.innerHTML = '';
    addClass([confirmSection, noStoresMessage, geolocateError], HIDDEN_CLASS);
    queryFirst('.find-store-submit', activeIspuContainer).disabled = true;
    if (isInCartPage()) {
        activeIspuContainer.dataset.editStore = 'false';
        delete activeIspuContainer.dataset.isOosReplacement;
        delete activeIspuContainer.dataset.excludeLineItemUUID;
    }
    // remove any active class from the pickup-in-store-content on closing the modal
    addClass(queryAll('.sheet-page.pickup-in-store-content'), HIDDEN_CLASS);
    removeClass(queryAll('.sheet-page.pickup-in-store-content'), ACTIVE_CLASS);
});

// Event listener to retrieve geo location based store results on click of use current location
ispuClosestParent.on('click', '.detect-location', event => {
    const { actionUrl } = event.currentTarget.dataset;
    const mixinData = { actionUrl, forceGeoLocation: true };
    const noStoresMessage = queryFirst('.store-locator-no-results', ispuContainer);
    const storeResults = queryFirst('.pick-up-in-stores', ispuContainer);
    queryAll('.find-store-input', queryFirst('.sheet.modal[id*="pick-up-in-store"]')).forEach(input => {
        input.value = '';
    });
    queryFirst('.find-store-submit', ispuContainer).disabled = true;
    geolocate(handleInStore, mixinData, () => {
        removeClass(queryFirst('.geo-location-error', ispuContainer), HIDDEN_CLASS);
        addClass([storeResults, noStoresMessage], HIDDEN_CLASS);
    });
});
